import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Globe, ChevronDown, ChevronRight, Package, Tag, Building2, Monitor, Settings, Flag, X, AlertCircle, Search, Info, Store, CreditCard, ArrowUp } from 'lucide-react';
import RegionCountrySelector from '../components/RegionCountrySelector';
import PlatformCards from '../components/PlatformCards';
import LocationSelector from '../components/LocationSelector';
import { ThemeToggle } from '../components/ThemeToggle';
import { VideoBackground } from '../components/VideoBackground';
import HomePageV4 from '../components/HomePageV4';
import OnboardingDetail from '../components/OnboardingDetail';
import { BaseModal } from '../components/BaseModal';
import FeedbackButton from '../components/FeedbackButton';
import Disclaimer from '../components/Disclaimer';
import AIAssistant from '../components/AIAssistant';
import { useProductSearch } from '../hooks/useProductSearch';
import { useTheme } from '../contexts/ThemeContext';
import { useTabGeneration } from '../hooks/useTabGeneration';
import { useProductProcessing } from '../hooks/useProductProcessing';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { cn } from '../lib/utils';
import { datadogRum } from '@datadog/browser-rum';
import { getVisibleCountries, hasComingSoonForCountry, sortByAvailability } from '../utils/tableHelpers';
import { sortPaymentMethods } from '../utils/paymentHelpers';
import { sortProductModules } from '../utils/softwareHelpers';
import { HardwareTableSection } from '../components/tables/HardwareTableSection';
import { SoftwareTableSection } from '../components/tables/SoftwareTableSection';
import { Level2PlatformTableSection } from '../components/tables/Level2PlatformTableSection';
import { PartnerIntegrationsTable } from '../components/tables/PartnerIntegrationsTable';
import { SearchResultsTable } from '../components/tables/SearchResultsTable';
import PlatformDetailsView from '../components/PlatformDetailsView';

export interface Product {
  id: number;
  platform: string;
  platform_description?: string;
  solution: string;
  solution_description?: string;
  development: string;
  hardware_family: string;
  model: string;
  patt: string;
  category: string;
  tag: string;
  vertical: string;
  solution_level: number | string;
  countries: Record<string, boolean | string>; // true = available, false = not available, "Coming Soon" = coming soon
  regions: string[];
  notes: string;
}

export interface OnboardingSystem {
  id: number;
  platform: string;
  country: string;
  gma: string;
  manual: string;
  sfeu_insights: string;
  cognito: string;
  sales_center: string;
  sf_usa: string;
  checkmate: string;
  manual_eu: string;
  s4eu_alloy: string;
  mp_lbm: string;
  ltm: string;
  mp_insights: string;
  partner_portal: string;
  pp_insights: string;
  cm_lbm: string;
  tsys: string;
  monthly_statements: string;
  lt_lbm: string;
  lt_insights: string;
}

export interface ColumnGroup {
  key: string;
  label: string;
}

export interface ColumnGroups {
  boardingSystems: ColumnGroup[];
  underwritingSystems: ColumnGroup[];
  merchantPortal: ColumnGroup[];
  partnerPortal: ColumnGroup[];
  chargebackManagement: ColumnGroup[];
  monthlyStatements: ColumnGroup[];
  liveTransactions: ColumnGroup[];
}

export interface SearchFiltersType {
  country?: string;
  region?: string;
  platform?: string;
  vertical?: string;
  development?: string;
  search?: string;
}

// Country configuration with flags and labels
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'Canada', name: 'Canada', flag: '🇨🇦' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'Ireland', name: 'Ireland', flag: '🇮🇪' },
  { code: 'Germany', name: 'Germany', flag: '🇩🇪' },
  { code: 'France', name: 'France', flag: '🇫🇷' },
  { code: 'Spain', name: 'Spain', flag: '🇪🇸' },
  { code: 'Italy', name: 'Italy', flag: '🇮🇹' },
  { code: 'Netherlands', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'Belgium', name: 'Belgium', flag: '🇧🇪' },
  { code: 'Austria', name: 'Austria', flag: '🇦🇹' },
  { code: 'Switzerland', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'Sweden', name: 'Sweden', flag: '🇸🇪' },
  { code: 'Norway', name: 'Norway', flag: '🇳🇴' },
  { code: 'Denmark', name: 'Denmark', flag: '🇩🇰' },
  { code: 'Finland', name: 'Finland', flag: '🇫🇮' },
  { code: 'Poland', name: 'Poland', flag: '🇵🇱' },
  { code: 'Czechia', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'Hungary', name: 'Hungary', flag: '🇭🇺' },
  { code: 'Australia', name: 'Australia', flag: '🇦🇺' },
  { code: 'New Zealand', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'Brazil', name: 'Brazil', flag: '🇧🇷' },
  { code: 'Israel', name: 'Israel', flag: '🇮🇱' }
];

// Helper function to render country availability (true, false, or "Coming Soon")
const renderCountryAvailability = (countries: Record<string, boolean | string>, countryCode: string) => {
  const value = countries[countryCode];
  return (
    <td key={countryCode} className="p-4 text-center" style={{width: '60px'}}>
      {value === true ? (
        <span className="text-green-600 font-semibold">✓</span>
      ) : value === 'Coming Soon' ? (
        <span className="text-transparent">-</span>
      ) : (
        <span className="text-red-500 font-semibold">✗</span>
      )}
    </td>
  );
};

// Helper functions moved to utils/tableHelpers.ts

export interface PlatformInfo {
  name: string;
  description: string;
  isAvailable: boolean;
  productCount: number;
  products: Product[];
}

// Region to country mapping
const REGION_COUNTRY_MAP: Record<string, string[]> = {
  'North America': ['US', 'Canada'],
  'Europe': ['UK', 'Ireland', 'Germany', 'Spain', 'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czechia', 'Denmark', 'Estonia', 'Finland', 'France', 'Greece', 'Hungary', 'Iceland', 'Italy', 'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Norway', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Sweden', 'Switzerland'],
  'APAC': ['Australia', 'New Zealand'],
  'Middle East': ['Israel'],
  'Latin America': ['Brazil']
};
// Function to get country flag emoji
const getCountryFlag = (countryName: string): string => {
  const country = countryName.toLowerCase();
  
  if (country.includes('united states') || country.includes('usa') || country === 'us') return '🇺🇸';
  if (country.includes('canada')) return '🇨🇦';
  if (country.includes('united kingdom') || country.includes('uk')) return '🇬🇧';
  if (country.includes('germany')) return '🇩🇪';
  if (country.includes('france')) return '🇫🇷';
  if (country.includes('italy')) return '🇮🇹';
  if (country.includes('spain')) return '🇪🇸';
  if (country.includes('netherlands')) return '🇳🇱';
  if (country.includes('belgium')) return '🇧🇪';
  if (country.includes('poland')) return '🇵🇱';
  if (country.includes('ireland')) return '🇮🇪';
  if (country.includes('austria')) return '🇦🇹';
  if (country.includes('portugal')) return '🇵🇹';
  if (country.includes('australia')) return '🇦🇺';
  if (country.includes('new zealand')) return '🇳🇿';
  if (country.includes('japan')) return '🇯🇵';
  if (country.includes('singapore')) return '🇸🇬';
  
  return '🌍'; // Default globe for unknown countries
};

const ProductMatrix: React.FC = () => {
  // Router hooks
  const { platformName, region, country, countryCode } = useParams<{
    platformName?: string;
    region?: string;
    country?: string;
    countryCode?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get all state from ProductMatrixContext
  const {
    // Navigation State
    selectedRegion,
    setSelectedRegion,
    selectedCountry,
    setSelectedCountry,
    selectedPlatform,
    setSelectedPlatform,
    currentView,
    setCurrentView,
    browseMode,
    setBrowseMode,

    // Product Data
    platforms,
    setPlatforms,
    platformProducts,
    setPlatformProducts,
    lbm2Products,
    setLbm2Products,
    onboardingSystems,
    setOnboardingSystems,
    onboardingColumnGroups,
    setOnboardingColumnGroups,

    // UI State
    platformLoading,
    setPlatformLoading,
    activeTab,
    setActiveTab,
    tabSearchQuery,
    setTabSearchQuery,
    highlightedProduct,
    setHighlightedProduct,
    showScrollToTop,
    setShowScrollToTop,
    expandedLevels,
    setExpandedLevels,
    isAIDrawerOpen,
    setIsAIDrawerOpen,
    aiDrawerWidth,

    // Search State
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching,
    showSearchDropdown,
    setShowSearchDropdown,

    // Options
    options,
    setOptions,

    // Modal State
    selectedDevice,
    showDeviceModal,
    handleDeviceClick,
    handleCloseDeviceModal,
    selectedPartnerProduct,
    showPartnerModal,
    handlePartnerClick,
    handleClosePartnerModal,
    selectedProduct,
    showProductModal,
    handleProductClick,
    handleCloseProductModal,
    selectedOnboardingCountry,
    showOnboardingModal,
    handleOnboardingClick,
    handleCloseOnboardingModal,
    setSelectedOnboardingCountry
  } = useProductMatrix();

  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { products, loading, error, searchProducts } = useProductSearch();
  const actualTheme = 'light'; // Force light mode

  // Search functionality
  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (query.trim().length === 0) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsSearching(false);
      return;
    }

    if (query.trim().length < 2) {
      return; // Don't search until at least 2 characters
    }

    setIsSearching(true);
    setShowSearchDropdown(true);

    try {
      await searchProducts({
        search: query.trim()
      });

      // Track search in Datadog
      if (datadogRum) {
        datadogRum.addAction(`search: "${query.trim()}"`, {
          search_query: query.trim(),
          search_length: query.trim().length,
          location: selectedCountry || 'browse_mode'
        });
      }

      // The searchProducts function updates the products state
      // We'll use those results for our search dropdown
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Update search results when products change (from search)
  useEffect(() => {
    if (searchQuery.trim().length >= 2 && products.length > 0) {
      // Limit to first 10 results for dropdown
      setSearchResults(products.slice(0, 10));
    }
  }, [products, searchQuery]);

  // Handle search result selection
  const handleSearchResultClick = async (product: Product) => {
    console.log('Search clicked for:', product.solution, 'Platform:', product.platform);

    // Track search result click in Datadog
    if (datadogRum) {
      datadogRum.addAction(`clicked: "${product.solution}" (${product.platform})`, {
        product_name: product.solution,
        platform: product.platform,
        category: product.category,
        development_status: product.development,
        location: selectedCountry || 'browse_mode'
      });
    }

    // Clear search first
    setSearchQuery('');
    setShowSearchDropdown(false);
    
    // Navigate to the platform URL
    const encodedPlatformName = encodeURIComponent(product.platform);
    navigate(`/platform/${encodedPlatformName}`);

    try {
      // Fetch ALL products for this platform (not filtered by geography)
      const params = new URLSearchParams();
      params.append('platform', product.platform);
      // Don't add country/region filters - we want all products

      const response = await fetch(`/api/products?${params.toString()}`);
      const data = await response.json();

      // Also fetch Level 2 platforms (solution_level = 2)
      const level2Params = new URLSearchParams();
      level2Params.append('solution_level', '2');

      const level2Response = await fetch(`/api/products?${level2Params.toString()}`);
      const level2Data = await level2Response.json();

      // Special case: Fetch additional platform products for specific platforms
      let integratedPaymentsData = null;
      let shift4PaymentsData = null;

      // Integrated Payments: Only for Shift4 Mobile (PATT partners for Supported Partners tab)
      if (product.platform === 'Shift4 Mobile') {
        const integratedPaymentsParams = new URLSearchParams();
        integratedPaymentsParams.append('platform', 'Integrated Payments');
        const integratedPaymentsResponse = await fetch(`/api/products?${integratedPaymentsParams.toString()}`);
        integratedPaymentsData = await integratedPaymentsResponse.json();
      }

      // Shift4 Payments: For all platforms with Supported Payments tab
      if (product.platform === 'SkyTab' || product.platform === 'SkyTab Venue' || product.platform === 'Shift4 Solo' || product.platform === 'Shift4 Mobile') {
        const shift4PaymentsParams = new URLSearchParams();
        shift4PaymentsParams.append('platform', 'Shift4 Payments');
        const shift4PaymentsResponse = await fetch(`/api/products?${shift4PaymentsParams.toString()}`);
        shift4PaymentsData = await shift4PaymentsResponse.json();
      }

      if (data.success) {
        let allProducts = data.data || [];

        // Add Level 2 platform products
        if (level2Data.success) {
          allProducts = [...allProducts, ...level2Data.data];
        }

        // Add Integrated Payments products for Shift4 Mobile (partners with PATT support)
        if (integratedPaymentsData && integratedPaymentsData.success) {
          const pattProducts = integratedPaymentsData.data.filter((p: any) => p.patt && p.patt.trim() !== '');
          allProducts = [...allProducts, ...pattProducts];
        }

        // Add Shift4 Payments products for Shift4 Mobile (payment methods)
        if (shift4PaymentsData && shift4PaymentsData.success) {
          allProducts = [...allProducts, ...shift4PaymentsData.data];
        }

        // Parse countries JSON string for each product
        const processedProducts = allProducts.map((p: any) => {
          let parsedCountries = {};
          try {
            parsedCountries = typeof p.countries === 'string' 
              ? JSON.parse(p.countries) 
              : p.countries || {};
          } catch (error) {
            console.warn('Failed to parse countries for product:', p.solution, error);
          }

          let parsedRegions = [];
          try {
            parsedRegions = typeof p.available_regions === 'string'
              ? JSON.parse(p.available_regions)
              : Array.isArray(p.available_regions)
              ? p.available_regions
              : [];
          } catch (error) {
            console.warn('Failed to parse regions for product:', p.solution, error);
          }

          return {
            ...p,
            countries: parsedCountries,
            regions: parsedRegions
          };
        });
        
        // Update platform products state directly
        setPlatformProducts(processedProducts);

        console.log('Loaded platform data with', processedProducts.length, 'products');

        // Tab will be set by useEffect after tabs are generated

        // Determine which tab the searched product belongs to
        const searchedProduct = processedProducts.find((p: Product) => 
          p.solution.toLowerCase() === product.solution.toLowerCase()
        );
        
        if (searchedProduct) {
          let targetTab = 'hardware'; // default
          
          if (searchedProduct.category === 'Hardware' && String(searchedProduct.solution_level) !== "2") {
            targetTab = 'hardware';
          } else if (searchedProduct.category !== 'Hardware' && String(searchedProduct.solution_level) !== "2") {
            targetTab = 'software';
          } else if (String(searchedProduct.solution_level) === "2") {
            // This is a Level 2 platform - find the correct tab key
            if (searchedProduct.platform === 'Shift4 Payments') {
              targetTab = 'level2-payments';
            } else {
              targetTab = `level2-${searchedProduct.platform.toLowerCase().replace(/\s+/g, '-')}`;
            }
          }
          
          console.log('Setting active tab to:', targetTab, 'for product category:', searchedProduct.category);
          setActiveTab(targetTab);
        }
        
        // Now highlight the specific product after a short delay (allowing tab switch to complete)
        setTimeout(() => {
          console.log('Looking for product elements with data-product-name...');
          const productElements = document.querySelectorAll('[data-product-name]');
          console.log('Found', productElements.length, 'product elements');
          
          productElements.forEach((element, index) => {
            const productName = element.getAttribute('data-product-name');
            console.log(`Element ${index}:`, productName);
            
            if (productName && productName.toLowerCase() === product.solution.toLowerCase()) {
              console.log('Found exact match, highlighting:', productName);
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Add a temporary highlight effect
              element.classList.add('!bg-blue-100', 'dark:!bg-blue-900/50', 'ring-2', 'ring-blue-500', 'ring-opacity-75');
              
              setTimeout(() => {
                element.classList.remove('!bg-blue-100', 'dark:!bg-blue-900/50', 'ring-2', 'ring-blue-500', 'ring-opacity-75');
              }, 4000);
            }
          });
        }, 500); // Increased delay to allow tab switching
      }
    } catch (error) {
      console.error('Failed to load platform products for search:', error);
    } finally {
      setPlatformLoading(false);
    }
  };

  // Load filter options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await fetch('/api/filters');
        const data = await response.json();
        if (data.success) {
          setOptions(data.data);
        }
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };

    loadOptions();
  }, []);

  // Sync component state with URL on mount and URL changes
  useEffect(() => {
    const pathname = location.pathname;

    // Handle route params for region/country (new clean URLs)
    if (region) setSelectedRegion(decodeURIComponent(region));
    if (country) setSelectedCountry(decodeURIComponent(country));

    // Handle query params for backward compatibility
    const queryRegion = searchParams.get('region');
    const queryCountry = searchParams.get('country');

    // Handle query parameter URLs - redirect to clean URLs or set view appropriately
    if (queryRegion && !region && pathname === '/') {
      // Root path with region query param should redirect to clean URL
      navigate(`/location/${encodeURIComponent(queryRegion)}`, { replace: true });
      return;
    }
    if (queryRegion && !region && pathname.startsWith('/location')) {
      // /location with region query param should redirect to clean URL
      navigate(`/location/${encodeURIComponent(queryRegion)}`, { replace: true });
      return;
    }
    if (queryCountry && !country && !region && pathname === '/') {
      // Root path with country query param should go to browse mode
      navigate(`/browse?country=${queryCountry}`, { replace: true });
      return;
    }

    // Determine current view based on pathname
    if (pathname.startsWith('/location')) {
      setCurrentView('location');
    } else if (pathname === '/browse') {
      setCurrentView('selection');
      setBrowseMode(true);
      // Set country from query param for browse mode
      if (queryCountry) setSelectedCountry(queryCountry);
    } else if (pathname.startsWith('/platform/') && platformName) {
      const decodedPlatformName = decodeURIComponent(platformName);
      setSelectedPlatform(decodedPlatformName);
      setCurrentView('platform');
      setBrowseMode(!queryCountry && !queryRegion); // Browse mode if no location filters
      // Set filters from query params for platform view
      if (queryCountry) setSelectedCountry(queryCountry);
      if (queryRegion) setSelectedRegion(queryRegion);
    } else if (pathname === '/onboarding') {
      // Onboarding country selection page
      setCurrentView('selection');
      setBrowseMode(true);
      setSelectedOnboardingCountry(null);
    } else if (pathname.startsWith('/onboarding/') && countryCode) {
      // Handle onboarding detail route
      const decodedCountryCode = decodeURIComponent(countryCode);
      // Map country codes to country names
      const countryNameMap: Record<string, string> = {
        'US': 'United States',
        'Germany': 'Germany'
      };
      const countryName = countryNameMap[decodedCountryCode] || decodedCountryCode;
      const countryInfo = COUNTRIES.find(c => c.code === decodedCountryCode || c.name === decodedCountryCode);
      setSelectedOnboardingCountry({
        country: decodedCountryCode,
        countryName: countryName,
        countryFlag: countryInfo?.flag || getCountryFlag(decodedCountryCode),
        systems: []
      });
      setCurrentView('onboarding');
    } else {
      // Root path - enable browse mode to show all platforms
      setCurrentView('selection');
      setBrowseMode(true);
      // Force reload products when navigating to home
      searchProducts({});
    }
  }, [location.pathname, searchParams, platformName, region, country, countryCode, navigate, searchProducts]);

  // Load products when region/country changes or browse mode is enabled
  useEffect(() => {
    console.log('Products loading effect triggered:', { selectedRegion, selectedCountry, browseMode });
    if (selectedRegion || selectedCountry || browseMode) {
      const filters: SearchFiltersType = {};
      if (selectedCountry) filters.country = selectedCountry;
      if (selectedRegion) filters.region = selectedRegion;

      console.log('Calling searchProducts with filters:', filters);
      searchProducts(filters);
    }
  }, [selectedRegion, selectedCountry, browseMode, searchProducts]);

  // CRITICAL FIX: Fetch both Level 1 and Level 2 products when platform is selected (for direct URL access)
  useEffect(() => {
    const fetchPlatformProducts = async () => {
      if (!selectedPlatform || currentView !== 'platform') return;

      // Clear search when navigating to a new platform
      setTabSearchQuery('');

      setPlatformLoading(true);
      try {
        // Fetch Level 1 platform products
        const params = new URLSearchParams();
        params.append('platform', selectedPlatform);
        // In browse mode, don't filter by geography - show all products for the platform
        if (!browseMode) {
          if (selectedCountry) params.append('country', selectedCountry);
          if (selectedRegion) params.append('region', selectedRegion);
        }
        const response = await fetch(`/api/products?${params.toString()}`);
        const data = await response.json();

        // Also fetch Level 2 platforms (solution_level = 2)
        const level2Params = new URLSearchParams();
        level2Params.append('solution_level', '2');
        // In browse mode, don't filter by geography for Level 2 platforms either
        if (!browseMode) {
          if (selectedCountry) level2Params.append('country', selectedCountry);
          if (selectedRegion) level2Params.append('region', selectedRegion);
        }

        const level2Response = await fetch(`/api/products?${level2Params.toString()}`);
        const level2Data = await level2Response.json();

        // Special case: Fetch additional platform products for specific platforms
        let integratedPaymentsData = null;
        let shift4PaymentsData = null;

        // Integrated Payments: Only for Shift4 Mobile (PATT partners for Supported Partners tab)
        if (selectedPlatform === 'Shift4 Mobile') {
          const integratedPaymentsParams = new URLSearchParams();
          integratedPaymentsParams.append('platform', 'Integrated Payments');
          // Don't filter by geography for Integrated Payments - show all PATT partners
          const integratedPaymentsResponse = await fetch(`/api/products?${integratedPaymentsParams.toString()}`);
          integratedPaymentsData = await integratedPaymentsResponse.json();
        }

        // Shift4 Payments: For all platforms with Supported Payments tab
        if (selectedPlatform === 'SkyTab' || selectedPlatform === 'SkyTab Venue' || selectedPlatform === 'Shift4 Solo' || selectedPlatform === 'Shift4 Mobile') {
          const shift4PaymentsParams = new URLSearchParams();
          shift4PaymentsParams.append('platform', 'Shift4 Payments');
          const shift4PaymentsResponse = await fetch(`/api/products?${shift4PaymentsParams.toString()}`);
          shift4PaymentsData = await shift4PaymentsResponse.json();
        }

        if (data.success) {
          let allProducts = data.data || [];

          // Add Level 2 platform products
          if (level2Data.success) {
            allProducts = [...allProducts, ...level2Data.data];
          }

          // Add Integrated Payments products for Shift4 Mobile (partners with PATT support)
          if (integratedPaymentsData && integratedPaymentsData.success) {
            // Filter for products with PATT values only
            const pattProducts = integratedPaymentsData.data.filter((p: any) => p.patt && p.patt.trim() !== '');
            allProducts = [...allProducts, ...pattProducts];
          }

          // Add Shift4 Payments products for Shift4 Mobile (payment methods)
          if (shift4PaymentsData && shift4PaymentsData.success) {
            allProducts = [...allProducts, ...shift4PaymentsData.data];
          }

          // Parse countries JSON string for each product
          const processedProducts = allProducts.map((product: any) => {
            let parsedCountries = {};
            try {
              parsedCountries = typeof product.countries === 'string'
                ? JSON.parse(product.countries)
                : product.countries || {};
            } catch (error) {
              console.error('Error parsing countries for product:', product, error);
              parsedCountries = {};
            }

            let parsedRegions = [];
            try {
              parsedRegions = typeof product.regions === 'string'
                ? JSON.parse(product.regions)
                : (Array.isArray(product.regions) ? product.regions : []);
            } catch (error) {
              console.error('Error parsing regions for product:', product, error);
              parsedRegions = [];
            }

            return {
              ...product,
              countries: parsedCountries,
              regions: parsedRegions
            };
          });

          setPlatformProducts(processedProducts);
          console.log('Loaded platform data via useEffect with', processedProducts.length, 'products');

          // Tab will be set by useEffect after tabs are generated
        }
      } catch (error) {
        console.error('Error fetching platform products in useEffect:', error);
        setPlatformProducts([]);
      } finally {
        setPlatformLoading(false);
      }
    };

    fetchPlatformProducts();

    // Special case: Fetch LBM 2.0 products when viewing SkyTab
    const fetchLbm2Products = async () => {
      if (selectedPlatform !== 'SkyTab' || currentView !== 'platform') {
        setLbm2Products([]);
        return;
      }

      try {
        const params = new URLSearchParams();
        params.append('platform', 'LBM 2.0');
        const response = await fetch(`/api/products?${params.toString()}`);
        const data = await response.json();

        if (data.success) {
          const processedLbm2Products = data.data.map((product: any) => {
            let parsedCountries = {};
            try {
              parsedCountries = typeof product.countries === 'string'
                ? JSON.parse(product.countries)
                : product.countries || {};
            } catch (error) {
              console.error('Error parsing countries for LBM 2.0 product:', product, error);
              parsedCountries = {};
            }

            return {
              ...product,
              countries: parsedCountries
            };
          });

          setLbm2Products(processedLbm2Products);
        }
      } catch (error) {
        console.error('Error fetching LBM 2.0 products:', error);
        setLbm2Products([]);
      }
    };

    fetchLbm2Products();
  }, [selectedPlatform, currentView, browseMode, selectedCountry, selectedRegion]);

  // Fetch onboarding data when viewing platform details
  useEffect(() => {
    const fetchOnboardingData = async () => {
      // Only fetch if we're viewing a platform that has onboarding data
      const platformsWithOnboarding = ['SkyTab', 'SkyTab Venue', 'Integrated Payments', 'Shift4 Mobile', 'Shift4 Solo'];

      if (selectedPlatform && platformsWithOnboarding.includes(selectedPlatform) && currentView === 'platform') {
        try {
          // Map platform names to onboarding database names
          let onboardingPlatformName = selectedPlatform;
          if (selectedPlatform === 'Integrated Payments') {
            onboardingPlatformName = '3PI';
          } else if (selectedPlatform === 'SkyTab') {
            onboardingPlatformName = 'Skytab'; // Note: lowercase 't' in onboarding DB
          } else if (selectedPlatform === 'SkyTab Venue') {
            onboardingPlatformName = 'Skytab Venue'; // Note: lowercase 't' in onboarding DB
          }

          const response = await fetch('/api/onboarding');
          const result = await response.json();

          if (result.success) {
            // Filter systems by platform (case-insensitive comparison)
            const filteredSystems = result.data.filter((system: OnboardingSystem) =>
              system.platform.toLowerCase() === onboardingPlatformName.toLowerCase()
            );
            setOnboardingSystems(filteredSystems);
            setOnboardingColumnGroups(result.columnGroups);
          }
        } catch (error) {
          console.error('Error fetching onboarding data:', error);
          setOnboardingSystems([]);
          setOnboardingColumnGroups(null);
        }
      } else {
        // Clear onboarding data when not viewing a relevant platform
        setOnboardingSystems([]);
        setOnboardingColumnGroups(null);
      }
    };

    fetchOnboardingData();
  }, [selectedPlatform, currentView]);

  // Handle highlight parameter from URL
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam) {
      setHighlightedProduct(highlightParam);
      // Clear highlight after 3 seconds
      const timeout = setTimeout(() => {
        setHighlightedProduct(null);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [searchParams]);

  // Scroll to highlighted product when it's set and products are loaded
  useEffect(() => {
    if (highlightedProduct && !platformLoading && platformProducts.length > 0) {
      console.log('[Scroll] Attempting to scroll to product:', highlightedProduct, 'on tab:', activeTab);

      // Decode the URL-encoded product name
      const decodedProductName = decodeURIComponent(highlightedProduct);

      // Retry logic: try to find element multiple times (like search does)
      const tryScroll = (attempt = 1, maxAttempts = 6) => {
        console.log(`[Scroll] Attempt ${attempt}/${maxAttempts} - Looking for: "${decodedProductName}"`);

        // Use querySelectorAll and case-insensitive matching (same as search)
        const productElements = document.querySelectorAll('[data-product-name]');
        console.log(`[Scroll] Found ${productElements.length} elements with data-product-name`);

        let found = false;
        productElements.forEach((element) => {
          const productName = element.getAttribute('data-product-name');
          if (productName && productName.toLowerCase() === decodedProductName.toLowerCase()) {
            console.log('[Scroll] Element found! Scrolling and highlighting...');
            found = true;

            element.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Add visual highlight (same as search results)
            element.classList.add('!bg-blue-100', 'dark:!bg-blue-900/50', 'ring-2', 'ring-blue-500', 'ring-opacity-75');

            // Remove highlight after 4 seconds
            setTimeout(() => {
              element.classList.remove('!bg-blue-100', 'dark:!bg-blue-900/50', 'ring-2', 'ring-blue-500', 'ring-opacity-75');
            }, 4000);
          }
        });

        // If not found and attempts remain, retry after delay
        if (!found && attempt < maxAttempts) {
          console.log(`[Scroll] Not found yet, retrying in 300ms...`);
          setTimeout(() => tryScroll(attempt + 1, maxAttempts), 300);
        } else if (!found) {
          console.warn('[Scroll] Element not found after', maxAttempts, 'attempts:', decodedProductName);
          console.log('[Scroll] Available products:',
            Array.from(productElements).map(el => el.getAttribute('data-product-name')).filter(Boolean)
          );
        }
      };

      // Start trying after initial delay
      const scrollTimeout = setTimeout(() => tryScroll(), 500);
      return () => clearTimeout(scrollTimeout);
    }
  }, [highlightedProduct, platformLoading, platformProducts.length, activeTab]);

  // Scroll-to-top button visibility
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    console.log('[ScrollToTop] useEffect running, scrollContainer:', scrollContainer);
    if (!scrollContainer) {
      console.log('[ScrollToTop] No scroll container found!');
      return;
    }

    const handleScroll = () => {
      // Show button when scrolled down more than 200px
      const scrollTop = scrollContainer.scrollTop;
      const shouldShow = scrollTop > 200;
      console.log('[ScrollToTop] scrollTop:', scrollTop, 'shouldShow:', shouldShow);
      setShowScrollToTop(shouldShow);
    };

    // Add scroll listener with throttling
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    console.log('[ScrollToTop] Adding scroll listener to container');
    scrollContainer.addEventListener('scroll', scrollListener);
    return () => {
      console.log('[ScrollToTop] Removing scroll listener');
      scrollContainer.removeEventListener('scroll', scrollListener);
    };
  }, [currentView, selectedPlatform]);

  // Process products into platform cards when products change
  useEffect(() => {
    if (products.length > 0) {
      const platformMap = new Map<string, PlatformInfo>();

      // Group products by platform (solution_level 1 only for cards)
      products
        .filter(product => String(product.solution_level) === "1")
        .forEach(product => {
          const platformName = product.platform;
          
          if (!platformMap.has(platformName)) {
            platformMap.set(platformName, {
              name: platformName,
              description: product.platform_description || '',
              isAvailable: false,
              productCount: 0,
              products: []
            });
          }

          const platform = platformMap.get(platformName)!;
          
          // Check if this product is available in selected region/country
          // In browse mode, all platforms are considered available
          const isProductAvailable = browseMode
            ? true
            : selectedCountry 
            ? product.countries[selectedCountry] 
            : selectedRegion 
            ? product.regions.includes(selectedRegion)
            : false;

          if (isProductAvailable) {
            platform.isAvailable = true;
          }

          platform.products.push(product);
          platform.productCount++;
        });

      const platformsArray = Array.from(platformMap.values());
      console.log('Setting platforms:', platformsArray.length, 'platforms');
      setPlatforms(platformsArray);
    } else {
      console.log('Setting empty platforms array');
      setPlatforms([]);
    }
  }, [products, selectedRegion, selectedCountry, browseMode]);

  const handleCountryClick = (countryCode: string, countryName: string) => {
    navigate(`/onboarding/${countryCode}`);
  };

  const handleBackFromOnboarding = () => {
    navigate('/onboarding');
  };

  const handlePlatformClick = async (platform: PlatformInfo) => {
    if (platform.isAvailable) {
      // Navigate to platform URL with current search params (region/country)
      const encodedPlatformName = encodeURIComponent(platform.name);
      const currentParams = new URLSearchParams(searchParams);
      const urlWithParams = currentParams.toString() ?
        `/platform/${encodedPlatformName}?${currentParams.toString()}` :
        `/platform/${encodedPlatformName}`;
      navigate(urlWithParams);

      try {
        // Fetch Level 1 platform products
        const params = new URLSearchParams();
        params.append('platform', platform.name);
        // In browse mode, don't filter by geography - show all products for the platform
        if (!browseMode) {
          if (selectedCountry) params.append('country', selectedCountry);
          if (selectedRegion) params.append('region', selectedRegion);
        }

        const response = await fetch(`/api/products?${params.toString()}`);
        const data = await response.json();

        // Also fetch Level 2 platforms (solution_level = 2)
        const level2Params = new URLSearchParams();
        level2Params.append('solution_level', '2');
        // In browse mode, don't filter by geography for Level 2 platforms either
        if (!browseMode) {
          if (selectedCountry) level2Params.append('country', selectedCountry);
          if (selectedRegion) level2Params.append('region', selectedRegion);
        }

        const level2Response = await fetch(`/api/products?${level2Params.toString()}`);
        const level2Data = await level2Response.json();

        // Special case: Fetch additional platform products for specific platforms
        let integratedPaymentsData = null;
        let shift4PaymentsData = null;

        // Integrated Payments: Only for Shift4 Mobile (PATT partners for Supported Partners tab)
        if (platform.name === 'Shift4 Mobile') {
          const integratedPaymentsParams = new URLSearchParams();
          integratedPaymentsParams.append('platform', 'Integrated Payments');
          const integratedPaymentsResponse = await fetch(`/api/products?${integratedPaymentsParams.toString()}`);
          integratedPaymentsData = await integratedPaymentsResponse.json();
        }

        // Shift4 Payments: For all platforms with Supported Payments tab
        if (platform.name === 'SkyTab' || platform.name === 'SkyTab Venue' || platform.name === 'Shift4 Solo' || platform.name === 'Shift4 Mobile') {
          const shift4PaymentsParams = new URLSearchParams();
          shift4PaymentsParams.append('platform', 'Shift4 Payments');
          const shift4PaymentsResponse = await fetch(`/api/products?${shift4PaymentsParams.toString()}`);
          shift4PaymentsData = await shift4PaymentsResponse.json();
        }

        if (data.success) {
          let allProducts = data.data || [];

          // Add Level 2 platform products
          if (level2Data.success) {
            allProducts = [...allProducts, ...level2Data.data];
          }

          // Add Integrated Payments products for Shift4 Mobile (partners with PATT support)
          if (integratedPaymentsData && integratedPaymentsData.success) {
            const pattProducts = integratedPaymentsData.data.filter((p: any) => p.patt && p.patt.trim() !== '');
            allProducts = [...allProducts, ...pattProducts];
          }

          // Add Shift4 Payments products for Shift4 Mobile (payment methods)
          if (shift4PaymentsData && shift4PaymentsData.success) {
            allProducts = [...allProducts, ...shift4PaymentsData.data];
          }

          // Parse countries JSON string for each product
          const processedProducts = allProducts.map((product: any) => {
            let parsedCountries = {};
            try {
              parsedCountries = typeof product.countries === 'string'
                ? JSON.parse(product.countries)
                : product.countries || {};
            } catch (error) {
              console.warn('Failed to parse countries for product:', product.solution, error);
              parsedCountries = {};
            }

            return {
              ...product,
              countries: parsedCountries
            };
          });

          setPlatformProducts(processedProducts);

          // Tab will be set by useEffect after tabs are generated
        }
      } catch (error) {
        console.error('Error fetching platform products:', error);
        setPlatformProducts([]);
      } finally {
        setPlatformLoading(false);
      }
    }
  };

  // Scroll to top handler
  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getDeviceImage = (solution: string, model?: string) => {
    const solutionLower = solution.toLowerCase();
    if (solutionLower.includes('pos terminal')) return '/skytab-pos-terminal.png';
    if (solutionLower.includes('kds')) return '/skytab-kds.png';
    if (solutionLower.includes('glass')) return '/skytab-glass.png';
    if (solutionLower.includes('air')) return '/skytab-air.png';
    if (solutionLower.includes('mobile')) return '/skytab-mobile.png';
    if (solutionLower.includes('kiosk')) return '/skytab-kiosk.png';
    if (solutionLower.includes('cfd') || solutionLower.includes('customer facing')) return '/skytab-cfd.png';
    return '/skytab-overview.png'; // Default fallback
  };

  const getPlatformImage = (platformName: string) => {
    const name = platformName.toLowerCase();

    if (name.includes('skytab pos')) {
      return '/skytab-overview.png';
    }
    if (name.includes('skytab venue')) {
      return '/skytab-venue.png';
    }
    if (name.includes('shift4 solo')) {
      return '/skytab-mobile.png';
    }
    if (name.includes('acquiring') || name.includes('shift4 acquiring')) {
      return '/shift4-logo.svg';
    }
    if (name.includes('lbm') || name.includes('hospitality')) {
      return '/shift4-logo.svg';
    }
    if (name.includes('marketplace') || name.includes('integrations')) {
      return '/shift4-logo.svg';
    }

    // Default fallback
    return '/shift4-logo.svg';
  };

  const handleBackToPlatforms = () => {
    // Navigate back to platform selection with current filters
    const params = new URLSearchParams();
    if (selectedRegion) params.append('region', selectedRegion);
    if (selectedCountry) params.append('country', selectedCountry);
    const queryString = params.toString();
    navigate(queryString ? `/?${queryString}` : '/');
  };

  const handleResetToInitial = () => {
    // Clear component state - route handler will set browseMode and currentView
    setSelectedRegion('');
    setSelectedCountry('');
    setSelectedPlatform(null);
    // Don't set browseMode here - let route handler do it to ensure proper product loading
    setPlatformProducts([]);
    setPlatformLoading(false);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setShowSearchDropdown(false);
    setActiveTab(''); // Reset to empty, will be set by useEffect

    // Navigate to home and clear all search params
    navigate('/');
  };

  const toggleLevel = (level: string) => {
    const newExpanded = new Set(expandedLevels);
    if (newExpanded.has(level)) {
      newExpanded.delete(level);
    } else {
      newExpanded.add(level);
    }
    setExpandedLevels(newExpanded);
  };

  const getLevelTitle = (level: string) => {
    switch (level) {
      case "1": return 'Core Solutions';
      case "2": return 'Extended Features';
      case "3": return 'Advanced Integrations';
      default: return `Level ${level} Solutions`;
    }
  };

  const getLevelDescription = (level: string) => {
    switch (level) {
      case "1": return 'Essential features and core functionality';
      case "2": return 'Enhanced capabilities and payment options';
      case "3": return 'Advanced integrations and specialized tools';
      default: return 'Additional solutions and features';
    }
  };

  // First pass: Process product data (without tabs for search results)
  // This provides the base data needed for tab generation
  const productProcessing1 = useProductProcessing({
    platformProducts,
    selectedPlatform,
    tabSearchQuery,
    products,
    tabs: undefined, // First pass without tabs
    COUNTRIES,
    selectedCountry,
    currentView,
    browseMode
  });

  // Generate tabs using the processed product data
  const tabs = useTabGeneration({
    selectedPlatform,
    platformProducts,
    products,
    hardwareProducts: productProcessing1.hardwareProducts,
    softwareProducts: productProcessing1.softwareProducts,
    unifiedVisibleCountries: productProcessing1.unifiedVisibleCountries,
    level2Platforms: productProcessing1.level2Platforms,
    lbm2Products,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry
  });

  // Second pass: Recompute with tabs for search results
  // The useMemo in useProductProcessing will only recompute when tabs changes
  const {
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    searchVisibleCountries,
    useCompactMode,
    level2Platforms,
    level2SearchResults,
    platformDescription,
    groupedSolutions,
    showPlatformSelection,
    showMainLanding
  } = useProductProcessing({
    platformProducts,
    selectedPlatform,
    tabSearchQuery,
    products,
    tabs, // Now tabs is available from useTabGeneration
    COUNTRIES,
    selectedCountry,
    currentView,
    browseMode
  });

  console.log('Unified visible countries:', unifiedVisibleCountries.length);
  console.log('Using compact mode:', useCompactMode);

  // Set active tab based on URL parameter or default to first tab
  React.useEffect(() => {
    if (tabs.length > 0 && selectedPlatform) {
      // Always check URL parameter first
      const tabParam = searchParams.get('tab');

      // Check if current activeTab is a header-level tab (Products, 3PI, Shift4 Payments)
      const isHeaderTab = ['Products', '3PI', 'Shift4 Payments'].includes(activeTab);

      if (tabParam && tabs.find(t => t.id === tabParam)) {
        // Valid tab parameter in URL - use it
        if (activeTab !== tabParam) {
          console.log('Setting active tab from URL:', tabParam);
          setActiveTab(tabParam);
        }
      } else if (!activeTab || !tabs.find(t => t.id === activeTab)) {
        // No valid URL parameter and (no active tab OR active tab doesn't exist in platform tabs) - use first tab
        // This includes the case where activeTab is a header-level tab but we need to show platform detail content
        console.log('Setting active tab to first available:', tabs[0].id);
        setActiveTab(tabs[0].id);
      }
    }
  }, [tabs.length, selectedPlatform, searchParams, activeTab]);


  // Group hardware by family for better organization
  // const groupedHardware = hardwareProducts.reduce((acc, product) => {
  //   const family = product.hardware_family || 'Other';
  //   if (!acc[family]) {
  //     acc[family] = [];
  //   }
  //   acc[family].push(product);
  //   return acc;
  // }, {} as Record<string, Product[]>);


  return (
    <>
      {/* Main Content - Shifts left when drawer opens */}
      <div
        className="h-screen relative text-foreground overflow-hidden transition-all duration-300"
        style={{
          marginRight: isAIDrawerOpen && window.innerWidth >= 1024 ? `${aiDrawerWidth}px` : '0'
        }}
      >

      {/* Full Screen Video Background - Always present */}
      <div className="absolute inset-0">
        <VideoBackground
          icon={showMainLanding ? Globe : undefined}
          title={showMainLanding ? "Product Catalog" : undefined}
          description={showMainLanding ? "Quickly see what's available in each region, the supported hardware, payment methods, integrations, and partner integration options — all in one place." : undefined}
          showOverlay={false}
          theme={actualTheme}
        />
      </div>

      {/* Global Disclaimer Banner */}
      <Disclaimer theme={actualTheme} isAIDrawerOpen={isAIDrawerOpen} aiPanelWidth={aiDrawerWidth} />

      {/* Home Page */}
      {showMainLanding && (
        <div className="pt-14">
          <HomePageV4
            platforms={platforms}
            onCountryClick={handleCountryClick}
            loading={loading}
            theme={actualTheme}
          />
        </div>
      )}


      {/* Location Selection View */}
      {currentView === 'location' && (
        <div className="pt-14">
          <LocationSelector options={options} />
        </div>
      )}

      {/* Onboarding View */}
      {currentView === 'onboarding' && selectedOnboardingCountry && (
        <div className="pt-14">
          <OnboardingDetail
            countryCode={selectedOnboardingCountry.country}
            countryName={selectedOnboardingCountry.countryName}
            onBack={handleBackFromOnboarding}
            theme={actualTheme}
          />
        </div>
      )}

      {/* Main Content Card - Only show for platform selection or platform details */}
      {(showPlatformSelection || currentView === 'platform') && (
        <div className={cn(
          "absolute flex items-center justify-center",
          currentView === 'platform'
            ? "top-14 bottom-0 left-0 right-0 p-0"
            : "inset-0 pt-28 lg:pt-20 p-4 lg:p-8"
        )}>
        <div className={cn(
          "w-full flex flex-col overflow-hidden relative",
          currentView === 'platform' ? "max-w-none" : "max-w-6xl",
          currentView === 'platform' ? "bg-card h-full" : "h-full lg:h-[80vh]",
          currentView === 'platform'
            ? "lg:bg-card lg:rounded-none lg:shadow-none lg:border-0"
            : ""
        )}>

          {/* Background Accent Elements - Only show on platform detail pages */}
          {currentView === 'platform' && (
            <>
              {/* Dot Grid Background Accent */}
              <div
                className="absolute top-0 right-0 w-72 h-72 opacity-0 pointer-events-none"
                style={{
                  backgroundImage: 'radial-gradient(circle, #9CA3AF 2px, transparent 2px)',
                  backgroundSize: '24px 24px'
                }}
              />

              {/* Blue Accent Circle */}
              <div
                className="absolute top-[-75px] right-48 w-40 h-40 rounded-full opacity-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, #1d4ed8, #1d4ed8)',
                  filter: 'blur(0.5px)'
                }}
              />

              {/* Aqua Border Circle - Left side, partially clipped */}
              <div
                className="absolute top-1/2 left-[-150px] w-64 h-64 rounded-full opacity-0 pointer-events-none"
                style={{
                  border: '40px solid #6BD8FF',
                  background: 'transparent'
                }}
              />
            </>
          )}

          {/* Floating Card Content */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scrollbar-thin">
            <div className={cn(
              "p-6 lg:p-8"
            )}>




              {/* Platform Details View */}
              {currentView === 'platform' && selectedPlatform && (
                <PlatformDetailsView
                  COUNTRIES={COUNTRIES}
                  getCountryFlag={getCountryFlag}
                />
              )}

              {/* Platform Cards - Only show for platform selection */}
              {showPlatformSelection && (
                <>
                  {/* Back Button for Browse Mode */}
                  {browseMode && (
                    <div className="mb-6">
                      <button
                        onClick={() => {
                          if (selectedCountry) {
                            // If user came from location flow, go back to location selector
                            setSelectedCountry('');
                            setBrowseMode(false);
                            setCurrentView('location');
                            navigate('/location');
                          } else {
                            // Otherwise go back to home
                            setBrowseMode(false);
                            setCurrentView('selection');
                            navigate('/');
                          }
                        }}
                        className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
                      >
                        <ChevronDown className="w-4 h-4 rotate-90" />
                        {selectedCountry ? 'Back to location selector' : 'Back to home'}
                      </button>
                    </div>
                  )}
                  
                  
                  {loading && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="relative">
                        <div className=" rounded-full h-10 w-10 border-4 border-muted border-t-primary"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-pulse"></div>
                      </div>
                      <div className="mt-4 text-center">
                        <p className="text-base font-medium text-foreground animate-pulse">
                          Loading solutions...
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Discovering available products
                        </p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="animate-slide-in-from-top">
                      <div className="glass-card border-destructive/20 p-4 mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-destructive animate-pulse"></div>
                          <div className="text-destructive font-medium text-sm">
                            Error loading solutions
                          </div>
                        </div>
                        <p className="text-destructive/80 mt-2 text-sm">{error}</p>
                      </div>
                    </div>
                  )}

                  {!loading && !error && platforms.length > 0 && (
                    <div className="">
                      <div className="mb-8 mt-8 text-left">
                        <p className="text-lg text-white/90 font-medium text-left">
                          Select a product platform to explore
                        </p>
                      </div>

                      <div className="animate-slide-in-from-bottom">
                        <PlatformCards
                          platforms={platforms}
                          onPlatformClick={handlePlatformClick}
                        />
                      </div>
                    </div>
                  )}

                  {!loading && !error && platforms.length === 0 && (
                    <div className="text-center py-12 ">
                      <div className="glass-card p-6">
                        <div className="text-4xl mb-4 opacity-50">🔍</div>
                        <h3 className="heading-sm mb-2 text-muted-foreground">
                          No solutions found
                        </h3>
                        <p className="body-sm mb-6">
                          No products are currently available in the selected {selectedCountry ? 'country' : 'region'}.
                        </p>
                        <button
                          onClick={handleResetToInitial}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10" />
                          </svg>
                          Start Over
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Detail Modal - Using General Product Modal Style */}
      {showDeviceModal && selectedDevice && (
        <BaseModal
          isOpen={true}
          onClose={handleCloseDeviceModal}
          title={selectedDevice.solution}
          subtitle="Hardware Details"
          maxWidth="5xl"
        >
          <div className="space-y-4">
              {/* Platform */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Platform</h3>
                <p className="text-sm text-muted-foreground">{selectedDevice.platform}</p>
              </div>

              {/* Solution Description */}
              {selectedDevice.solution_description && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Description</h3>
                  <p className="text-sm text-muted-foreground">{selectedDevice.solution_description}</p>
                </div>
              )}

              {/* Hardware Family */}
              {selectedDevice.hardware_family && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Hardware Family</h3>
                  <p className="text-sm text-muted-foreground">{selectedDevice.hardware_family}</p>
                </div>
              )}

              {/* Model */}
              {selectedDevice.model && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Model</h3>
                  <p className="text-sm text-muted-foreground">{selectedDevice.model}</p>
                </div>
              )}

              {/* Tag */}
              {selectedDevice.tag && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Tag</h3>
                  <p className="text-sm text-muted-foreground">{selectedDevice.tag}</p>
                </div>
              )}

              {/* Vertical */}
              {selectedDevice.vertical && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Vertical</h3>
                  <p className="text-sm text-muted-foreground">{selectedDevice.vertical}</p>
                </div>
              )}

              {/* Development Status */}
              {selectedDevice.development && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Development Status</h3>
                  <p className="text-sm text-muted-foreground">{selectedDevice.development}</p>
                </div>
              )}

              {/* Country Availability */}
              {selectedDevice.countries && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-2">Country Availability</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {unifiedVisibleCountries.map(country => {
                      const availability = selectedDevice.countries[country.code];
                      return (
                        <div key={country.code} className="flex items-center gap-2 text-sm">
                          <span className="text-base">{country.flag}</span>
                          <span className="text-muted-foreground">{country.code}</span>
                          <span>
                            {availability === true ? (
                              <span className="text-green-600 font-semibold">✓</span>
                            ) : availability === 'Coming Soon' ? (
                              <span className="text-yellow-600 text-xs italic">Soon</span>
                            ) : (
                              <span className="text-red-500 font-semibold">✗</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Notes */}
            {selectedDevice.notes && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Additional Notes</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedDevice.notes}</p>
              </div>
            )}
          </div>
        </BaseModal>
      )}

      {/* Integrated Payments Product Detail Modal */}
      {showPartnerModal && selectedPartnerProduct && (
        <BaseModal
          isOpen={true}
          onClose={handleClosePartnerModal}
          title={selectedPartnerProduct.solution}
          maxWidth="2xl"
        >
          <div className="space-y-4">
              {/* Platform */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Platform</h3>
                <p className="text-sm text-muted-foreground">{selectedPartnerProduct.platform}</p>
              </div>

              {/* Solution Description */}
              {selectedPartnerProduct.solution_description && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Description</h3>
                  <p className="text-sm text-muted-foreground">{selectedPartnerProduct.solution_description}</p>
                </div>
              )}

              {/* Vertical */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Vertical</h3>
                <p className="text-sm text-muted-foreground">{selectedPartnerProduct.vertical}</p>
              </div>

              {/* Tag */}
              {selectedPartnerProduct.tag && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-1">Tag</h3>
                  <p className="text-sm text-muted-foreground">{selectedPartnerProduct.tag}</p>
                </div>
              )}

              {/* Country Availability */}
              {selectedPartnerProduct.countries && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-2">Country Availability</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {unifiedVisibleCountries.map(country => {
                      const availability = selectedPartnerProduct.countries[country.code];
                      return (
                        <div key={country.code} className="flex items-center gap-2 text-sm">
                          <span className="text-base">{country.flag}</span>
                          <span className="text-muted-foreground">{country.code}</span>
                          <span>
                            {availability === true ? (
                              <span className="text-green-600 font-semibold">✓</span>
                            ) : availability === 'Coming Soon' ? (
                              <span className="text-yellow-600 text-xs italic">Soon</span>
                            ) : (
                              <span className="text-red-500 font-semibold">✗</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Notes */}
            {selectedPartnerProduct.notes && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">Additional Notes</h3>
                <div className="bg-muted/50 border border-border rounded-md p-3">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{selectedPartnerProduct.notes}</p>
                </div>
              </div>
            )}
          </div>
        </BaseModal>
      )}

      {/* General Product Detail Modal */}
      {showProductModal && selectedProduct && (
        <BaseModal
          isOpen={true}
          onClose={handleCloseProductModal}
          title={selectedProduct.solution}
          subtitle="Product Details"
          maxWidth="5xl"
        >
          {(() => {
              // Check if this is a SkyTab device that should have an image
              const getDeviceImage = (solution: string, model: string) => {
                const solutionLower = solution.toLowerCase();
                if (solutionLower.includes('pos terminal')) return '/skytab-pos-terminal.png';
                if (solutionLower.includes('kds')) return '/skytab-kds.png';
                if (solutionLower.includes('glass')) return '/skytab-glass.png';
                if (solutionLower.includes('air')) return '/skytab-air.png';
                // Exclude product modules like Mobile Ordering and Mobile Wallet
                if (solutionLower.includes('mobile ordering') || solutionLower.includes('mobile wallet')) return null;
                if (solutionLower.includes('mobile')) return '/skytab-mobile.png';
                if (solutionLower.includes('kiosk')) return '/skytab-kiosk.png';
                if (solutionLower.includes('cfd') || solutionLower.includes('customer facing')) return '/skytab-cfd.png';
                return null; // No image available
              };

              const deviceImage = getDeviceImage(selectedProduct.solution, selectedProduct.model || '');
              const isSkytabDevice = selectedProduct.platform?.includes('SkyTab') && deviceImage;

              if (isSkytabDevice) {
                // Two-column layout for SkyTab devices with images
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column - Device Image */}
                    <div className="space-y-4">
                      <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-lg p-6 flex items-center justify-center min-h-[300px]">
                        <img
                          src={deviceImage}
                          alt={selectedProduct.solution}
                          className="max-w-full max-h-[280px] object-contain rounded-lg"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/skytab-overview.png';
                          }}
                        />
                      </div>
                    </div>

                    {/* Right Column - Device Details */}
                    <div className="space-y-4">
                      {/* Platform */}
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-1">Platform</h3>
                        <p className="text-sm text-muted-foreground">{selectedProduct.platform}</p>
                      </div>

                      {/* Solution Description */}
                      {selectedProduct.solution_description && (
                        <div>
                          <h3 className="text-sm font-medium text-foreground mb-1">Description</h3>
                          <p className="text-sm text-muted-foreground">{selectedProduct.solution_description}</p>
                        </div>
                      )}

                      {/* Vertical */}
                      {selectedProduct.vertical && (
                        <div>
                          <h3 className="text-sm font-medium text-foreground mb-1">Vertical</h3>
                          <p className="text-sm text-muted-foreground">{selectedProduct.vertical}</p>
                        </div>
                      )}

                      {/* Development Status */}
                      {selectedProduct.development && (
                        <div>
                          <h3 className="text-sm font-medium text-foreground mb-1">Development Status</h3>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                            selectedProduct.development === 'Ready'
                              ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                              : selectedProduct.development === 'In Pilot'
                              ? 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800'
                              : 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                          }`}>
                            {selectedProduct.development}
                          </span>
                        </div>
                      )}

                      {/* Country Availability */}
                      {selectedProduct.countries && (
                        <div>
                          <h3 className="text-sm font-medium text-foreground mb-2">Country Availability</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {unifiedVisibleCountries.map(country => {
                              const availability = selectedProduct.countries[country.code];
                              return (
                                <div key={country.code} className="flex items-center gap-2 text-sm">
                                  <span className="text-base">{country.flag}</span>
                                  <span className="text-muted-foreground">{country.code}</span>
                                  <span>
                                    {availability === true ? (
                                      <span className="text-green-600 font-semibold">✓</span>
                                    ) : availability === 'Coming Soon' ? (
                                      <span className="text-yellow-600 text-xs italic">Soon</span>
                                    ) : (
                                      <span className="text-red-500 font-semibold">✗</span>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {selectedProduct.notes && (
                        <div>
                          <h3 className="text-sm font-medium text-foreground mb-2">Additional Notes</h3>
                          <div className="bg-muted/50 border border-border rounded-md p-3">
                            <p className="text-sm text-foreground whitespace-pre-wrap">{selectedProduct.notes}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                // Single-column layout for non-SkyTab products or products without images
                return (
                  <div className="space-y-4">
                    {/* Platform */}
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-1">Platform</h3>
                      <p className="text-sm text-muted-foreground">{selectedProduct.platform}</p>
                    </div>

                    {/* Solution Description */}
                    {selectedProduct.solution_description && (
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-1">Description</h3>
                        <p className="text-sm text-muted-foreground">{selectedProduct.solution_description}</p>
                      </div>
                    )}

                    {/* Vertical */}
                    {selectedProduct.vertical && (
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-1">Vertical</h3>
                        <p className="text-sm text-muted-foreground">{selectedProduct.vertical}</p>
                      </div>
                    )}

                    {/* Development Status */}
                    {selectedProduct.development && (
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-1">Development Status</h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                          selectedProduct.development === 'Ready'
                            ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                            : selectedProduct.development === 'In Pilot'
                            ? 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800'
                            : 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                        }`}>
                          {selectedProduct.development}
                        </span>
                      </div>
                    )}

                    {/* Country Availability */}
                    {selectedProduct.countries && (
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-2">Country Availability</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {unifiedVisibleCountries.map(country => {
                            const availability = selectedProduct.countries[country.code];
                            return (
                              <div key={country.code} className="flex items-center gap-2 text-sm">
                                <span className="text-base">{country.flag}</span>
                                <span className="text-muted-foreground">{country.code}</span>
                                <span>
                                  {availability === true ? (
                                    <span className="text-green-600 font-semibold">✓</span>
                                  ) : availability === 'Coming Soon' ? (
                                    <span className="text-yellow-600 text-xs italic">Soon</span>
                                  ) : (
                                    <span className="text-red-500 font-semibold">✗</span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {selectedProduct.notes && (
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-2">Additional Notes</h3>
                        <div className="bg-muted/50 border border-border rounded-md p-3">
                          <p className="text-sm text-foreground whitespace-pre-wrap">{selectedProduct.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            })()}
        </BaseModal>
      )}

      {/* Onboarding Details Modal */}
      {showOnboardingModal && selectedOnboardingCountry && onboardingColumnGroups && (
        <BaseModal
          isOpen={true}
          onClose={handleCloseOnboardingModal}
          title={`${selectedOnboardingCountry.countryFlag} ${selectedOnboardingCountry.countryName}`}
          subtitle="Onboarding & Operational Systems"
          maxWidth="5xl"
        >
          <div className="grid grid-cols-2 gap-6">
                {/* Boarding Systems */}
                {onboardingColumnGroups.boardingSystems.length > 0 && (() => {
                  const items = onboardingColumnGroups.boardingSystems
                    .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                    .map(col => col.label);
                  if (items.length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">Boarding Systems</h3>
                      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                    </div>
                  );
                })()}

                {/* Underwriting Systems */}
                {onboardingColumnGroups.underwritingSystems.length > 0 && (() => {
                  const items = onboardingColumnGroups.underwritingSystems
                    .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                    .map(col => col.label);
                  if (items.length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">Underwriting Systems</h3>
                      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                    </div>
                  );
                })()}

                {/* Merchant Portal */}
                {onboardingColumnGroups.merchantPortal.length > 0 && (() => {
                  const items = onboardingColumnGroups.merchantPortal
                    .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                    .map(col => col.label);
                  if (items.length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">Merchant Portal</h3>
                      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                    </div>
                  );
                })()}

                {/* Partner Portal */}
                {onboardingColumnGroups.partnerPortal.length > 0 && (() => {
                  const items = onboardingColumnGroups.partnerPortal
                    .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                    .map(col => col.label);
                  if (items.length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">Partner Portal</h3>
                      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                    </div>
                  );
                })()}

                {/* Chargeback Management */}
                {onboardingColumnGroups.chargebackManagement.length > 0 && (() => {
                  const items = onboardingColumnGroups.chargebackManagement
                    .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                    .map(col => col.label);
                  if (items.length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">Chargeback Management</h3>
                      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                    </div>
                  );
                })()}

                {/* Monthly Statements */}
                {onboardingColumnGroups.monthlyStatements.length > 0 && (() => {
                  const items = onboardingColumnGroups.monthlyStatements
                    .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                    .map(col => col.label);
                  if (items.length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">Monthly Statements</h3>
                      <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                    </div>
                  );
                })()}

            {/* Live Transactions */}
            {onboardingColumnGroups.liveTransactions.length > 0 && (() => {
              const items = onboardingColumnGroups.liveTransactions
                .filter(col => selectedOnboardingCountry.systems[0]?.[col.key as keyof OnboardingSystem] === 'Y')
                .map(col => col.label);
              if (items.length === 0) return null;
              return (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Live Transactions</h3>
                  <p className="text-sm text-muted-foreground">{items.join(', ')}</p>
                </div>
              );
            })()}
          </div>
        </BaseModal>
      )}

      {/* Feedback Button */}
      <FeedbackButton feedbackEmail="amy.chou@shift4.com" isAIDrawerOpen={isAIDrawerOpen} />

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-6 md:bottom-20 md:right-6 w-12 h-12 md:w-14 md:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out flex items-center justify-center z-40 group"
          aria-label="Scroll to top"
          style={{
            animation: 'fadeIn 0.3s ease-in-out'
          }}
        >
          <ArrowUp className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform duration-200" />
        </button>
      )}
      </div>

      {/* AI Assistant Drawer - Always mounted for chat history persistence */}
      <AIAssistant
        isOpen={isAIDrawerOpen}
        onClose={() => setIsAIDrawerOpen(false)}
      />
    </>
  );
};

export default ProductMatrix;